/**
 * JackpotWheel Component
 *
 * Secondary wheel that appears when JACKPOT is hit on the main wheel.
 * Determines the percentage of the pool the player wins (1-25%).
 */

import { memo, useState, useEffect, useCallback, useRef } from 'react';
import { JACKPOT_WHEEL_SEGMENTS } from '../../types/jackpot';
import type { JackpotWheelResult, SegmentColor } from '../../types/jackpot';
import { useTranslation } from '@/hooks/useTranslation';

interface JackpotWheelProps {
  poolAmount: number;
  result?: JackpotWheelResult | null;
  onComplete: (result: JackpotWheelResult) => void;
  className?: string;
}

/**
 * Get segment fill color
 */
function getSegmentFill(color: SegmentColor, percentage: number): string {
  if (percentage >= 25) {
    return 'url(#jackpot-gold-gradient)';
  }
  if (percentage >= 15) {
    return 'url(#jackpot-silver-gradient)';
  }

  const colorMap: Record<SegmentColor, string> = {
    blue: '#3B82F6',
    green: '#10B981',
    purple: '#8B5CF6',
    orange: '#F59E0B',
    red: '#EF4444',
    gold: '#FFD700',
  };

  return colorMap[color];
}

/**
 * Calculate segment path for the jackpot wheel
 */
function getSegmentPath(
  index: number,
  totalSegments: number,
  radius: number,
  centerX: number,
  centerY: number
): string {
  const anglePerSegment = (2 * Math.PI) / totalSegments;
  const startAngle = index * anglePerSegment - Math.PI / 2;
  const endAngle = startAngle + anglePerSegment;

  const x1 = centerX + radius * Math.cos(startAngle);
  const y1 = centerY + radius * Math.sin(startAngle);
  const x2 = centerX + radius * Math.cos(endAngle);
  const y2 = centerY + radius * Math.sin(endAngle);

  const largeArcFlag = anglePerSegment > Math.PI ? 1 : 0;

  return `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
}

/**
 * Calculate text position
 */
function getTextPosition(
  index: number,
  totalSegments: number,
  radius: number,
  centerX: number,
  centerY: number
): { x: number; y: number; rotation: number } {
  const anglePerSegment = (2 * Math.PI) / totalSegments;
  const midAngle = index * anglePerSegment - Math.PI / 2 + anglePerSegment / 2;

  const textRadius = radius * 0.7;
  const x = centerX + textRadius * Math.cos(midAngle);
  const y = centerY + textRadius * Math.sin(midAngle);

  const rotation = (midAngle * 180) / Math.PI + 90;

  return { x, y, rotation };
}

/**
 * Helper sleep function with cancellation support
 */
function sleep(ms: number): { promise: Promise<void>; cancel: () => void } {
  let timeoutId: ReturnType<typeof setTimeout>;
  const promise = new Promise<void>((resolve) => {
    timeoutId = setTimeout(resolve, ms);
  });
  return {
    promise,
    cancel: () => clearTimeout(timeoutId),
  };
}

export const JackpotWheel = memo(function JackpotWheel({
  poolAmount,
  result,
  onComplete,
  className = '',
}: JackpotWheelProps) {
  const { t } = useTranslation();
  const [rotation, setRotation] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showExplosion, setShowExplosion] = useState(false);
  const isMountedRef = useRef(true);
  const sleepCancelRef = useRef<(() => void) | null>(null);

  const totalSegments = JACKPOT_WHEEL_SEGMENTS.length;
  const radius = 120;
  const centerX = 130;
  const centerY = 130;

  // Track mounted state and cleanup pending sleeps
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (sleepCancelRef.current) {
        sleepCancelRef.current();
      }
    };
  }, []);

  // Handle spin when result is provided
  useEffect(() => {
    if (result && !isAnimating) {
      handleSpin(result);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  const handleSpin = useCallback(
    async (spinResult: JackpotWheelResult) => {
      setIsAnimating(true);

      // Dramatic pause before spinning
      const pause = sleep(1000);
      sleepCancelRef.current = pause.cancel;
      await pause.promise;
      if (!isMountedRef.current) return;

      // Calculate rotation (slower, more tension)
      const fullSpins = 3;
      const targetDegrees = spinResult.animation_degrees;
      const totalRotation = rotation + fullSpins * 360 + targetDegrees;

      setRotation(totalRotation);

      // Wait for spin to complete (6 seconds for more tension)
      const spinWait = sleep(6000);
      sleepCancelRef.current = spinWait.cancel;
      await spinWait.promise;
      if (!isMountedRef.current) return;

      // Explosion effect
      setShowExplosion(true);
      const explosionWait = sleep(500);
      sleepCancelRef.current = explosionWait.cancel;
      await explosionWait.promise;
      sleepCancelRef.current = null;
      if (!isMountedRef.current) return;

      setIsAnimating(false);
      onComplete(spinResult);
    },
    [rotation, onComplete]
  );

  return (
    <div className={`relative ${className}`}>
      {/* Entrance animation wrapper */}
      <div className="animate-slide-up">
        {/* Pointer at top */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 z-10">
          <div
            className="w-0 h-0
                       border-l-[10px] border-r-[10px] border-t-[16px]
                       border-l-transparent border-r-transparent border-t-yellow-300
                       drop-shadow-lg"
          />
        </div>

        {/* Wheel container */}
        <div
          className="w-64 h-64 rounded-full shadow-2xl"
          style={{
            transform: `rotate(${rotation}deg)`,
            transition: isAnimating ? 'transform 6s cubic-bezier(0.17, 0.67, 0.08, 0.99)' : 'none',
            boxShadow: '0 0 30px rgba(255, 215, 0, 0.5)',
          }}
        >
          <svg viewBox="0 0 260 260" className="w-full h-full">
            {/* Gradient definitions */}
            <defs>
              <linearGradient id="jackpot-gold-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FFD700">
                  <animate
                    attributeName="stop-color"
                    values="#FFD700;#FFA500;#FFD700"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                </stop>
                <stop offset="100%" stopColor="#FFA500">
                  <animate
                    attributeName="stop-color"
                    values="#FFA500;#FFD700;#FFA500"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                </stop>
              </linearGradient>
              <linearGradient id="jackpot-silver-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#C0C0C0" />
                <stop offset="50%" stopColor="#E8E8E8" />
                <stop offset="100%" stopColor="#C0C0C0" />
              </linearGradient>
            </defs>

            {/* Outer ring with glow */}
            <circle
              cx={centerX}
              cy={centerY}
              r={radius + 8}
              fill="none"
              stroke="#FFD700"
              strokeWidth="3"
            />

            {/* Segments */}
            {JACKPOT_WHEEL_SEGMENTS.map((segment, index) => {
              const path = getSegmentPath(index, totalSegments, radius, centerX, centerY);
              const textPos = getTextPosition(index, totalSegments, radius, centerX, centerY);
              const fill = getSegmentFill(segment.color, segment.percentage);

              return (
                <g key={segment.id}>
                  <path d={path} fill={fill} stroke="#1e293b" strokeWidth="1" />
                  <text
                    x={textPos.x}
                    y={textPos.y}
                    transform={`rotate(${textPos.rotation}, ${textPos.x}, ${textPos.y})`}
                    fill={segment.percentage >= 15 ? '#1e293b' : '#ffffff'}
                    fontSize="10"
                    fontWeight="bold"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="select-none"
                  >
                    {segment.percentage}%
                  </text>
                </g>
              );
            })}

            {/* Center decoration */}
            <circle cx={centerX} cy={centerY} r="35" fill="#1e293b" />
            <circle cx={centerX} cy={centerY} r="32" fill="url(#jackpot-gold-gradient)" />

            {/* WIN text */}
            <text
              x={centerX}
              y={centerY}
              fill="#1e293b"
              fontSize="14"
              fontWeight="bold"
              textAnchor="middle"
              dominantBaseline="middle"
            >
              WIN!
            </text>
          </svg>
        </div>

        {/* Pool amount display */}
        <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-center whitespace-nowrap">
          <span className="text-sm text-slate-400">{t('jackpot.pool')} </span>
          <span className="text-lg font-bold text-amber-400">{poolAmount.toLocaleString()}</span>
          <span className="text-sm text-slate-400"> {t('jackpot.tokensLabel')}</span>
        </div>
      </div>

      {/* Explosion effect */}
      {showExplosion && (
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute inset-0 rounded-full animate-ping"
            style={{
              background: 'radial-gradient(circle, rgba(255,215,0,0.6) 0%, rgba(255,215,0,0) 70%)',
            }}
          />
        </div>
      )}
    </div>
  );
});
